import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

import { cn } from '@/design-system/lib/cn';

/**
 * Diálogo central sobre Radix Dialog: foco preso, Esc fecha a camada mais alta e
 * `aria-*` vêm do primitivo. Largura padrão de 500px; quem usa ajusta por `className`.
 */
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

function Overlay({ className }: { className?: string }) {
  return (
    <DialogPrimitive.Overlay
      className={cn(
        'fixed inset-0 z-[70] bg-overlay/40',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
        className
      )}
    />
  );
}

export type DialogContentProps = ComponentProps<typeof DialogPrimitive.Content> & {
  /** `alertdialog` para confirmação destrutiva. */
  role?: 'dialog' | 'alertdialog';
  /**
   * Liga o `aria-describedby` à descrição do cabeçalho. Desligado por padrão porque
   * nem todo diálogo tem descrição, e o Radix avisa quando o vínculo aponta para nada.
   */
  described?: boolean;
  /**
   * No celular, abre em tela cheia (como o painel do evento), com cabeçalho no topo,
   * rodapé embaixo e corpo rolável. Padrão: true para diálogos com conteúdo (role !== 'alertdialog').
   */
  fullscreenMobile?: boolean;
  /** Classe extra para a camada de fundo (overlay), útil em diálogos aninhados. */
  overlayClassName?: string;
};

export function DialogContent({
  className,
  children,
  described = false,
  role = 'dialog',
  fullscreenMobile,
  overlayClassName,
  ...props
}: DialogContentProps) {
  const isFullscreenMobile = fullscreenMobile ?? role !== 'alertdialog';

  return (
    <DialogPrimitive.Portal>
      <Overlay className={overlayClassName} />
      <DialogPrimitive.Content
        role={role}
        data-slot="dialog-content"
        {...(described ? {} : { 'aria-describedby': undefined })}
        className={cn(
          'fixed z-[71] flex flex-col bg-dialog outline-none',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0',
          isFullscreenMobile
            ? [
                // No celular, abre em tela cheia sem bordas ou margens:
                'max-md:!inset-0 max-md:!top-0 max-md:!right-0 max-md:!bottom-0 max-md:!left-0',
                'max-md:!h-dvh max-md:!max-h-dvh max-md:!w-full max-md:!max-w-none',
                'max-md:!translate-x-0 max-md:!translate-y-0',
                'max-md:!rounded-none max-md:!border-0 max-md:!shadow-none',
                'max-md:!m-0',
                // No computador, modal centralizado com cantos arredondados e sombra:
                'md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2',
                'md:max-h-[calc(100dvh-40px)] md:w-[min(500px,calc(100vw-24px))]',
                'md:rounded-dialog md:border md:border-line-dialog md:shadow-dialog',
                'md:data-[state=closed]:zoom-out-95 md:data-[state=open]:zoom-in-95',
              ]
            : [
                // Modal compacto centralizado em qualquer dispositivo:
                'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
                'max-h-[calc(100dvh-40px)] w-[min(500px,calc(100vw-24px))]',
                'rounded-dialog border border-line-dialog shadow-dialog',
                'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
              ],
          className
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

/** Cabeçalho com título, subtítulo opcional e o botão de fechar. */
export function DialogHeader({
  title,
  description,
  children,
  bordered = true,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  bordered?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        // O fechar não define a altura: o título fica centrado com ele e o respiro é igual
        // em cima e embaixo.
        'flex shrink-0 gap-3 px-5 py-4 max-md:px-4 max-md:py-3.5 max-md:pt-[calc(0.875rem+env(safe-area-inset-top))]',
        description ? 'items-start' : 'items-center',
        bordered && 'border-b border-line-divider',
        className
      )}
    >
      <div className="min-w-0 flex-1">
        <DialogPrimitive.Title className="text-lead font-bold text-ink">{title}</DialogPrimitive.Title>
        {description ? (
          <DialogPrimitive.Description className="mt-1 text-meta text-ink-subtle">
            {description}
          </DialogPrimitive.Description>
        ) : null}
        {children}
      </div>
      <DialogPrimitive.Close
        aria-label="Fechar"
        className="-my-1.5 -mr-2 grid size-9 shrink-0 place-items-center rounded-control-sm text-ink-secondary hover:bg-surface-muted"
      >
        <X aria-hidden className="size-4 stroke-[1.8]" />
      </DialogPrimitive.Close>
    </div>
  );
}

export function DialogBody({ className, ...props }: ComponentProps<'div'>) {
  const hasCustomPadding = className && /(^|\s)(p[xytb]?-|p-)/.test(className);

  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto',
        hasCustomPadding ? '' : 'px-5 py-4.5 max-md:px-4 max-md:py-3.5',
        className
      )}
      {...props}
    />
  );
}

export function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex shrink-0 flex-wrap justify-end gap-2 border-t border-line-divider px-5 py-3 max-md:mt-auto max-md:px-4 max-md:py-3 max-md:pb-[calc(0.75rem+env(safe-area-inset-bottom))]',
        className
      )}
      {...props}
    />
  );
}

export const DialogTitle = DialogPrimitive.Title;
export const DialogDescription = DialogPrimitive.Description;
